import { Megaphone, RefreshCw, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { PageHeader } from "@/client/components/page-header";
import { Badge } from "@/client/components/ui/badge";
import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import { Input } from "@/client/components/ui/input";
import { Skeleton } from "@/client/components/ui/skeleton";
import {
  type DocSourceSummary,
  docsAdminApi,
  type OutlineCollectionSummary,
  type TenantSummary,
} from "@/client/features/admin/docs-admin-api";
import { ApiError } from "@/client/lib/api";
import { displayDocTitle } from "@/shared/doc-title";

const selectClass =
  "border-input bg-transparent focus-visible:border-ring focus-visible:ring-ring/50 h-9 w-full rounded-md border px-3 py-1 text-sm shadow-xs focus-visible:ring-[3px] focus-visible:outline-none";

function SourcesTable({
  sources,
  busyId,
  slackConfigured,
  onSync,
  onNotify,
  onDelete,
}: {
  sources: DocSourceSummary[];
  busyId: string | null;
  slackConfigured: boolean;
  onSync: (id: string) => void;
  onNotify: (id: string) => void;
  onDelete: (source: DocSourceSummary) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-muted-foreground border-b text-left">
            <th className="py-2 pr-4 font-medium">Title</th>
            <th className="py-2 pr-4 font-medium">Type</th>
            <th className="py-2 pr-4 font-medium">Last synced</th>
            <th className="py-2 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sources.map((s) => (
            <tr key={s.id} className="border-b last:border-0">
              <td className="py-2 pr-4">
                <div className="font-medium">
                  {s.type === "document" ? displayDocTitle(s.title) : s.title}
                </div>
                {s.lastSyncError ? (
                  <div className="text-destructive text-xs">Sync error: {s.lastSyncError}</div>
                ) : null}
              </td>
              <td className="py-2 pr-4">
                <Badge variant="outline">{s.type}</Badge>
              </td>
              <td className="text-muted-foreground py-2 pr-4">
                {s.lastSyncedAt ? new Date(s.lastSyncedAt).toLocaleString() : "Never"}
              </td>
              <td className="py-2">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busyId === s.id}
                    onClick={() => onSync(s.id)}
                  >
                    <RefreshCw className="size-3.5" />
                    {busyId === s.id ? "Syncing…" : "Sync"}
                  </Button>
                  {slackConfigured ? (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busyId === s.id}
                      onClick={() => onNotify(s.id)}
                      title="Post a 'new documentation available' note to this tenant's Slack channel"
                    >
                      <Megaphone className="size-3.5" />
                      Announce
                    </Button>
                  ) : null}
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busyId === s.id}
                    onClick={() => onDelete(s)}
                  >
                    <Trash2 className="size-3.5" />
                    Remove
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AddSourceForm({
  tenantId,
  collections,
  collectionsUnavailable,
  onCreated,
}: {
  tenantId: string;
  collections: OutlineCollectionSummary[];
  collectionsUnavailable: boolean;
  onCreated: () => void;
}) {
  const [type, setType] = useState<"collection" | "document">("collection");
  const [collectionId, setCollectionId] = useState("");
  const [documentId, setDocumentId] = useState("");
  const [documentTitle, setDocumentTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    let payload;
    if (type === "collection") {
      const collection = collections.find((col) => col.id === collectionId);
      if (!collection) {
        toast.error("Select a collection to publish.");
        return;
      }
      payload = { tenantId, type, outlineId: collection.id, title: collection.name };
    } else {
      if (!documentId.trim() || !documentTitle.trim()) {
        toast.error("Enter the document id and a title.");
        return;
      }
      payload = { tenantId, type, outlineId: documentId.trim(), title: documentTitle.trim() };
    }
    setSubmitting(true);
    try {
      await docsAdminApi.createSource(payload);
      toast.success("Published. Initial sync started.");
      setCollectionId("");
      setDocumentId("");
      setDocumentTitle("");
      onCreated();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to publish.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Publish documentation</CardTitle>
        <CardDescription>
          Map an Outline collection or document to this tenant. Content is snapshotted into the
          portal; customers never reach Outline.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
          <label className="flex-1">
            <span className="mb-1 block text-sm font-medium">Source type</span>
            <select
              className={selectClass}
              value={type}
              onChange={(e) => setType(e.target.value as "collection" | "document")}
            >
              <option value="collection">Collection</option>
              <option value="document">Single document</option>
            </select>
          </label>

          {type === "collection" ? (
            <label className="flex-[2]">
              <span className="mb-1 block text-sm font-medium">Collection</span>
              {collectionsUnavailable ? (
                <p className="text-muted-foreground py-2 text-sm">
                  Outline isn't configured, so collections can't be listed. Set OUTLINE_API_TOKEN to
                  enable.
                </p>
              ) : (
                <select
                  className={selectClass}
                  value={collectionId}
                  onChange={(e) => setCollectionId(e.target.value)}
                >
                  <option value="">Select a collection…</option>
                  {collections.map((col) => (
                    <option key={col.id} value={col.id}>
                      {col.name}
                    </option>
                  ))}
                </select>
              )}
            </label>
          ) : (
            <>
              <label className="flex-1">
                <span className="mb-1 block text-sm font-medium">Document id</span>
                <Input
                  value={documentId}
                  onChange={(e) => setDocumentId(e.target.value)}
                  placeholder="Outline document id"
                />
              </label>
              <label className="flex-1">
                <span className="mb-1 block text-sm font-medium">Title</span>
                <Input
                  value={documentTitle}
                  onChange={(e) => setDocumentTitle(e.target.value)}
                  placeholder="Display title"
                />
              </label>
            </>
          )}

          <Button className="sm:mt-6" onClick={submit} disabled={submitting}>
            {submitting ? "Publishing…" : "Publish"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function StaffDocsPage() {
  const [tenants, setTenants] = useState<TenantSummary[] | null>(null);
  const [tenantsError, setTenantsError] = useState(false);
  const [tenantId, setTenantId] = useState("");
  const [collections, setCollections] = useState<OutlineCollectionSummary[]>([]);
  const [collectionsUnavailable, setCollectionsUnavailable] = useState(false);
  const [sources, setSources] = useState<DocSourceSummary[] | null>(null);
  const [sourcesError, setSourcesError] = useState(false);
  const [slackConfigured, setSlackConfigured] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ tenants: ts }, collectionsRes] = await Promise.all([
          docsAdminApi.listTenants(),
          docsAdminApi.listCollections().catch((err) => {
            if (err instanceof ApiError && err.status === 503) {
              setCollectionsUnavailable(true);
              return { collections: [] };
            }
            throw err;
          }),
        ]);
        setTenants(ts);
        setTenantsError(false);
        setCollections(collectionsRes.collections);
        if (ts.length > 0) setTenantId(ts[0]!.id);
      } catch (err) {
        // Keep tenants null so the UI shows an error rather than "No tenants yet".
        setTenantsError(true);
        toast.error(err instanceof ApiError ? err.message : "Failed to load admin data.");
      }
    })();
  }, []);

  // Tracks the currently selected tenant so an in-flight listSources response
  // for a previously selected tenant can be ignored (avoids a slow earlier
  // request overwriting the table with another tenant's sources).
  const latestTenantId = useRef(tenantId);
  useEffect(() => {
    latestTenantId.current = tenantId;
  }, [tenantId]);

  const refreshSources = useCallback(async () => {
    if (!tenantId) {
      setSources([]);
      setSourcesError(false);
      return;
    }
    const forTenant = tenantId;
    try {
      const res = await docsAdminApi.listSources(forTenant);
      if (latestTenantId.current !== forTenant) return;
      setSources(res.sources);
      setSlackConfigured(res.slackConfigured);
      setSourcesError(false);
    } catch (err) {
      if (latestTenantId.current !== forTenant) return;
      // Preserve any list we already have; a failed refresh shouldn't wipe it.
      setSourcesError(true);
      toast.error(err instanceof ApiError ? err.message : "Failed to load sources.");
    }
  }, [tenantId]);

  useEffect(() => {
    setSources(null);
    setSourcesError(false);
    void refreshSources();
  }, [refreshSources]);

  const onSync = async (id: string) => {
    setBusyId(id);
    try {
      const res = await docsAdminApi.syncSource(id);
      toast.success(`Synced ${res.pages} page(s), ${res.assets} image(s).`);
      await refreshSources();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Sync failed.");
      await refreshSources();
    } finally {
      setBusyId(null);
    }
  };

  const onNotify = async (id: string) => {
    setBusyId(id);
    try {
      const res = await docsAdminApi.notifySource(id);
      if (res.status === "sent") {
        toast.success("Posted to the tenant's Slack channel.");
      } else if (res.status === "skipped") {
        toast.message("No Slack channel mapped for this tenant.");
      } else {
        toast.error("Slack post failed.");
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to announce.");
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (source: DocSourceSummary) => {
    const title = source.type === "document" ? displayDocTitle(source.title) : source.title;
    if (!window.confirm(`Remove "${title}" from this tenant's portal?`)) return;
    setBusyId(source.id);
    try {
      await docsAdminApi.deleteSource(source.id);
      toast.success("Removed.");
      await refreshSources();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to remove.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Documentation"
        description="Publish Outline collections and documents to customer portals."
      />

      <Card>
        <CardHeader>
          <CardTitle>Tenant</CardTitle>
          <CardDescription>Choose which customer to manage.</CardDescription>
        </CardHeader>
        <CardContent>
          {tenantsError ? (
            <p className="text-muted-foreground text-sm">
              Couldn't load tenants. Refresh to try again.
            </p>
          ) : tenants === null ? (
            <Skeleton className="h-9 w-full max-w-sm" />
          ) : tenants.length === 0 ? (
            <p className="text-muted-foreground text-sm">No tenants yet.</p>
          ) : (
            <select
              className={`${selectClass} max-w-sm`}
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
            >
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          )}
        </CardContent>
      </Card>

      {tenantId ? (
        <>
          <AddSourceForm
            tenantId={tenantId}
            collections={collections}
            collectionsUnavailable={collectionsUnavailable}
            onCreated={refreshSources}
          />

          <Card>
            <CardHeader>
              <CardTitle>Published sources</CardTitle>
              <CardDescription>Outline content available in this tenant's portal.</CardDescription>
            </CardHeader>
            <CardContent>
              {sources && sources.length > 0 ? (
                <SourcesTable
                  sources={sources}
                  busyId={busyId}
                  slackConfigured={slackConfigured}
                  onSync={onSync}
                  onNotify={onNotify}
                  onDelete={onDelete}
                />
              ) : sourcesError ? (
                <p className="text-muted-foreground text-sm">
                  Couldn't load published sources. Refresh to try again.
                </p>
              ) : sources === null ? (
                <div className="flex flex-col gap-2">
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-full" />
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">Nothing published yet.</p>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
